import React, { useContext, useReducer } from 'react';

import { ErrorMessage } from 'types/shared/error-message.type';
import { addDatadogRumError } from 'utils/datadog-rum';

enum ErrorHandlerContextActionTypes {
  ADD_ERRORS = 'ADD_ERRORS',
  REMOVE_ERROR = 'REMOVE_ERROR',
  CLEAR_ERRORS = 'CLEAR_ERRORS',
}

type Action =
  | { type: ErrorHandlerContextActionTypes.ADD_ERRORS; payload: { errors?: ErrorMessage[] } }
  | { type: ErrorHandlerContextActionTypes.REMOVE_ERROR; payload: string }
  | { type: ErrorHandlerContextActionTypes.CLEAR_ERRORS };

type Dispatch = (action: Action) => void;

type State = {
  errors: ErrorMessage[];
};

const ErrorHandlerContextStateContext = React.createContext<State | undefined>(undefined);
const ErrorHandlerContextDispatchContext = React.createContext<Dispatch | undefined>(undefined);

const initialState: State = {
  errors: [],
};

function ErrorHandlerContextReducer(state: State, action: Action): State {
  switch (action.type) {
    case ErrorHandlerContextActionTypes.ADD_ERRORS:
      return {
        errors: action.payload.errors ?? [],
      };

    case ErrorHandlerContextActionTypes.REMOVE_ERROR:
      return {
        errors: state.errors.filter((err) => err.field !== action.payload),
      };

    case ErrorHandlerContextActionTypes.CLEAR_ERRORS:
      return initialState;

    default:
      return state;
  }
}

type ErrorHandlerContextProviderProps = {
  children: React.ReactNode;
};

const ErrorHandlerContextProvider: React.FC<ErrorHandlerContextProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(ErrorHandlerContextReducer, initialState);

  const dispatchWithRum: Dispatch = (action) => {
    if (action.type === ErrorHandlerContextActionTypes.ADD_ERRORS) {
      action.payload.errors?.forEach((error) => addDatadogRumError(new Error(error.message), { field: error.field, code: error.code }));
    }
    dispatch(action);
  };

  return (
    <ErrorHandlerContextStateContext.Provider value={state}>
      <ErrorHandlerContextDispatchContext.Provider value={dispatchWithRum}>{children}</ErrorHandlerContextDispatchContext.Provider>
    </ErrorHandlerContextStateContext.Provider>
  );
};

function useErrorHandlerContextState() {
  const context = useContext(ErrorHandlerContextStateContext);
  if (context === undefined) {
    throw new Error('useErrorHandlerContextState must be used within a ErrorHandlerContextProvider');
  }
  return context;
}

function useErrorHandlerContextDispatch() {
  const context = useContext(ErrorHandlerContextDispatchContext);
  if (context === undefined) {
    throw new Error('useErrorHandlerContextDispatch must be used within a ErrorHandlerContextProvider');
  }
  return context;
}

export { ErrorHandlerContextProvider, useErrorHandlerContextState, useErrorHandlerContextDispatch, ErrorHandlerContextActionTypes };
